import { downloadQrCard } from "../lib/qrCardGenerator";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Building2,
  Wrench,
  ClipboardCheck,
  QrCode,
  ArrowRight,
  MapPin,
  Calendar,
  Star,
  Loader2,
  Plus,
  ImageIcon
} from "lucide-react";
import { toast } from "sonner";

const ClinicDetails = () => {
  const { clinicId } = useParams();
  const { user, token } = useAuth();
  const [clinic, setClinic] = useState(null);
  const [elements, setElements] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [biomedDevices, setBiomedDevices] = useState([]);
  const [instrumentsData, setInstrumentsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [clinicId, token]);

  const fetchData = async () => {
    try {
      const [clinicRes, elementsRes, evaluationsRes, requestsRes, biomedRes] = await Promise.all([
        axios.get(`${API}/clinics/${clinicId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/elements?clinic_id=${clinicId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/evaluations?clinic_id=${clinicId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/maintenance?clinic_id=${clinicId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/biomed-devices?clinic_id=${clinicId}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setClinic(clinicRes.data);
      setElements(elementsRes.data);
      setEvaluations(evaluationsRes.data);
      setMaintenanceRequests(requestsRes.data);
      setBiomedDevices(biomedRes.data || []);
      axios.get(`${API}/instruments/by-clinic/${clinicId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setInstrumentsData(r.data)).catch(() => {});
    } catch (error) {
      toast.error("فشل في تحميل بيانات العيادة");
    } finally {
      setLoading(false);
    }
  };

  const getConditionLabel = (condition) => {
    const labels = { good: "جيدة", needs_maintenance: "تحتاج صيانة", critical: "حرجة" };
    return labels[condition] || condition;
  };

  const getStatusLabel = (status) => {
    const labels = { pending: "قيد الانتظار", scheduled: "مجدول", in_progress: "قيد التنفيذ", completed: "مكتمل", cancelled: "ملغي" };
    return labels[status] || status;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      wall: "الجدران", floor: "الأرضيات", ceiling: "السقف", lighting: "الإضاءة",
      plumbing: "السباكة", electrical: "الكهرباء", dental_chair: "كرسي الأسنان",
      xray: "جهاز الأشعة", sterilizer: "جهاز التعقيم", compressor: "الضاغط",
      suction: "جهاز الشفط", medical_equipment: "أجهزة طبية أخرى", it_equipment: "جهاز إلكتروني", other: "أخرى"
    };
    return labels[category] || category;
  };

  const structuralElements = elements.filter(e => e.element_type === "structural");
  const deviceElements = elements.filter(e => e.element_type === "device");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="text-center py-16">
        <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <h3 className="text-lg font-semibold text-[#655dc6] mb-2">العيادة غير موجودة</h3>
        <Link to="/clinics">
          <Button variant="outline">العودة للعيادات</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clinic-details-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/clinics" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
          <ArrowRight className="w-5 h-5 text-[#655dc6]" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">{clinic.name}</h1>
          <div className="flex items-center gap-2 text-slate-500 mt-1">
            <MapPin className="w-4 h-4" />
            <span>{clinic.floor}</span>
            {clinic.building && <span>• {clinic.building}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {(user?.role === "admin" || user?.role === "inspector") && (
            <Link to={`/evaluation/${clinicId}`}>
              <Button className="bg-[#9b8fd4] hover:bg-[#8B6BA8] text-white" data-testid="evaluate-btn">
                <ClipboardCheck className="w-4 h-4 ml-2" />
                تقييم
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-3">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-[#655dc6]">{structuralElements.length}</p>
            <p className="text-sm text-slate-500">عنصر إنشائي</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
              <Wrench className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-[#655dc6]">{biomedDevices.length}</p>
            <p className="text-sm text-slate-500">جهاز طبي</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
              <ClipboardCheck className="w-6 h-6 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-[#655dc6]">{evaluations.length}</p>
            <p className="text-sm text-slate-500">تقييم</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mx-auto mb-3">
              <Wrench className="w-6 h-6 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-[#655dc6]">{maintenanceRequests.filter(r => r.status !== "completed").length}</p>
            <p className="text-sm text-slate-500">طلب صيانة نشط</p>
          </CardContent>
        </Card>
      </div>

      {/* QR Code */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="qr-code-container">
              <img
                src={`data:image/png;base64,${clinic.qr_code}`}
                alt={`QR Code for ${clinic.name}`}
                className="w-32 h-32"
              />
            </div>
            <div className="text-center sm:text-right flex-1">
              <h3 className="text-lg font-semibold text-[#655dc6] mb-2">رمز QR للعيادة</h3>
              <p className="text-slate-500 mb-4">يمكن للممرضة مسح هذا الرمز للإبلاغ عن مشاكل الصيانة</p>
              <Button
                variant="outline"
                onClick={async () => {
                  await downloadQrCard({
                    qrBase64: clinic.qr_code,
                    name: clinic.name,
                    floor: clinic.floor,
                    building: clinic.building
                  });
                }}
              >
                <QrCode className="w-4 h-4 ml-2" />
                تحميل رمز QR
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="elements" className="w-full">
        <TabsList className="w-full flex justify-end bg-white border shadow-sm rounded-lg p-1">
          <TabsTrigger value="instruments" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white" data-testid="clinic-instruments-tab">
            Sterile Instruments
          </TabsTrigger>
          <TabsTrigger value="evaluations" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">
            التقييمات
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">
            طلبات الصيانة
          </TabsTrigger>
          <TabsTrigger value="elements" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">
            العناصر
          </TabsTrigger>
        </TabsList>

        {/* Elements Tab */}
        <TabsContent value="elements" className="mt-6">
          <div className="space-y-6">
            {/* Structural Elements */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-end">
                <CardTitle className="text-lg text-[#655dc6] flex items-center gap-2">
                  العناصر الإنشائية ({structuralElements.length})
                  <Building2 className="w-5 h-5 text-[#9b8fd4]" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {structuralElements.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {structuralElements.map((element) => (
                      <div key={element.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-[#655dc6]">{element.name}</h4>
                          <Badge className={`condition-${element.condition}`}>
                            {getConditionLabel(element.condition)}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 mb-2">{getCategoryLabel(element.category)}</p>
                        {element.images?.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-slate-400">
                            <ImageIcon className="w-3 h-3" />
                            <span>{element.images.length} صورة</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">لا توجد عناصر إنشائية مسجلة</p>
                )}
              </CardContent>
            </Card>

            {/* Biomed Devices (new BioMed inventory) */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-end">
                <CardTitle className="text-lg text-[#655dc6] flex items-center gap-2">
                  الأجهزة الطبية ({biomedDevices.length})
                  <Wrench className="w-5 h-5 text-[#9b8fd4]" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {biomedDevices.length > 0 ? (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm" data-testid="clinic-biomed-table">
                      <thead className="bg-slate-50 text-right">
                        <tr>
                          <th className="p-3 font-medium text-slate-600">BioMed No.</th>
                          <th className="p-3 font-medium text-slate-600">Device Name</th>
                          <th className="p-3 font-medium text-slate-600">Manufacturer</th>
                          <th className="p-3 font-medium text-slate-600">Model</th>
                          <th className="p-3 font-medium text-slate-600">Serial</th>
                          <th className="p-3 font-medium text-slate-600">آخر تقييم</th>
                        </tr>
                      </thead>
                      <tbody>
                        {biomedDevices.map((d) => (
                          <tr key={d.id} className="border-t hover:bg-slate-50" data-testid={`clinic-biomed-row-${d.biomed_no}`}>
                            <td className="p-3 font-mono font-semibold text-[#4a3fa8]">{d.biomed_no}</td>
                            <td className="p-3 font-medium">{d.device_name}</td>
                            <td className="p-3">{d.manufacturer}</td>
                            <td className="p-3 font-mono text-xs">{d.model_no}</td>
                            <td className="p-3 font-mono text-xs">{d.serial_no}</td>
                            <td className="p-3">
                              {d.latest_status === "operational" && <Badge className="bg-emerald-100 text-emerald-800 border-0">Operational</Badge>}
                              {d.latest_status === "needs_maintenance" && <Badge className="bg-amber-100 text-amber-800 border-0">Needs Maintenance</Badge>}
                              {d.latest_status === "out_of_service" && <Badge className="bg-red-100 text-red-800 border-0">Out of Service</Badge>}
                              {!d.latest_status && <span className="text-slate-400 text-xs">لم يُقيّم بعد</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="p-2 border-t bg-slate-50 text-left">
                      <Link to="/biomed-devices" className="text-xs text-[#655dc6] hover:underline">
                        فتح صفحة الأجهزة الطبية →
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-500 mb-3">لا توجد أجهزة طبية مسجلة</p>
                    <Link to="/biomed-devices">
                      <Button variant="outline" size="sm" data-testid="go-to-biomed-page-btn">
                        <Plus className="w-4 h-4 ml-1" /> إدارة الأجهزة الطبية
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Instruments Tab */}
        <TabsContent value="instruments" className="mt-6">
          <div dir="ltr" className="space-y-6 text-left">
            <Card className="border-0 shadow-sm">
              <CardContent className="py-5">
                <div>
                  <h3 className="font-semibold text-[#655dc6]">Instrument Requests via Clinic QR</h3>
                  <p className="text-sm text-slate-500 mt-1">The nurse scans the clinic QR code (same maintenance QR) and picks "Request Instruments" — delivery is handled by the Sterile Storage account</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-[#655dc6] flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-[#9b8fd4]" />
                  Clinic Instruments ({instrumentsData?.items?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {instrumentsData?.items?.length > 0 ? (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm" data-testid="clinic-instruments-table">
                      <thead className="bg-slate-50 text-left">
                        <tr>
                          <th className="p-3 font-medium text-slate-600">Instrument</th>
                          <th className="p-3 font-medium text-slate-600">Category</th>
                          <th className="p-3 font-medium text-slate-600 text-center">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {instrumentsData.items.map((it) => (
                          <tr key={it.id} className="border-t hover:bg-slate-50">
                            <td className="p-3 font-medium text-xs">{it.name}</td>
                            <td className="p-3 text-[10px] text-slate-500">{it.category}</td>
                            <td className="p-3 text-center font-bold text-[#655dc6]">{it.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">
                    {instrumentsData?.code ? "No instruments recorded for this clinic" : "This clinic is not linked to the instruments inventory"}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-[#655dc6] flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-[#9b8fd4]" />
                  Currently Borrowed from CSSD ({instrumentsData?.loans?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {instrumentsData?.loans?.length > 0 ? (
                  <div className="space-y-2">
                    {instrumentsData.loans.map((l) => (
                      <div key={l.id} className={`flex items-center justify-between rounded-lg p-3 ${l.overdue ? "bg-red-50" : "bg-amber-50"}`} data-testid={`clinic-loan-${l.id}`}>
                        <div>
                          <p className="text-xs font-medium">{l.instrument_name}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">By {l.borrowed_by_name} — {(l.borrowed_at || "").slice(0, 10)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {l.overdue && <Badge className="bg-red-500 text-white hover:bg-red-500">Overdue {l.days_out}d</Badge>}
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Qty {l.qty}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No instruments currently borrowed</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              {maintenanceRequests.length > 0 ? (
                <div className="space-y-4">
                  {maintenanceRequests.map((request) => (
                    <div key={request.id} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-[#655dc6]">{getCategoryLabel(request.category)}</h4>
                          <p className="text-sm text-slate-500 mt-1">{request.description}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={`status-${request.status}`}>
                            {getStatusLabel(request.status)}
                          </Badge>
                          <Badge className={`priority-${request.priority}`}>
                            {request.priority === "urgent" ? "عاجل" : request.priority === "high" ? "عالي" : request.priority === "medium" ? "متوسط" : "منخفض"}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-400 mt-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(request.created_at).toLocaleDateString("ar-SA")}
                        </span>
                        <span>بواسطة: {request.created_by_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">لا توجد طلبات صيانة</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evaluations Tab */}
        <TabsContent value="evaluations" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              {evaluations.length > 0 ? (
                <div className="space-y-4">
                  {evaluations.map((evaluation) => (
                    <div key={evaluation.id} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-sm text-slate-500">بواسطة: {evaluation.inspector_name}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(evaluation.created_at).toLocaleDateString("ar-SA")}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <p className="text-sm text-slate-500 mb-1">العناصر الإنشائية</p>
                          <div className="flex items-center justify-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-5 h-5 ${i < evaluation.structural_rating ? "text-[#9b8fd4] fill-current" : "text-slate-200"}`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <p className="text-sm text-slate-500 mb-1">الأجهزة الطبية</p>
                          <div className="flex items-center justify-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-5 h-5 ${i < evaluation.device_rating ? "text-[#9b8fd4] fill-current" : "text-slate-200"}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      {evaluation.overall_notes && (
                        <p className="mt-4 text-sm text-slate-600 bg-white p-3 rounded-lg">
                          {evaluation.overall_notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">لا توجد تقييمات سابقة</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClinicDetails;
